import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { VehicleDocumentType } from '../../../../../asset-overview/models/vehicle-documents.model';
import { VehicleDocumentsService } from '../../../../../asset-overview/services/vehicle-documents.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { LinkDocumentTypeResult, LinkedDocumentTypeUsage } from '../../../models/link-document-type.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../../_shared/pipes/lookup-label.pipe';
import { translateLookupLabel } from '../../../../../../_shared/helpers/lookup-label.helper';

@Component({
  selector: 'app-link-document-type-modal',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    TranslocoDirective,
    TranslocoPipe,
    LookupLabelPipe
  ],
  templateUrl: './link-document-type-modal.component.html',
  styleUrl: './link-document-type-modal.component.scss'
})
export class LinkDocumentTypeModalComponent implements OnInit, OnDestroy {
  /** Requirement the document type is being linked to. */
  @Input() requirementName: string = '';
  /** Types already linked to other requirements in the compliance being built. */
  @Input() usedTypes: LinkedDocumentTypeUsage[] = [];

  private activeModal = inject(NgbActiveModal);
  private vehicleDocumentsService = inject(VehicleDocumentsService);
  private errorService = inject(ErrorHandlerService);
  private readonly transloco = inject(TranslocoService);
  private readonly cancellation = new Subject<void>();

  /** Vehicle document type names are catalogue data; translated for display only. */
  protected readonly documentTypeLabelPrefix = 'assetOverview.documentTypeNames';

  documentTypes: VehicleDocumentType[] = [];
  selectedDocumentTypeId: number | null = null;
  isLoading: boolean = true;

  get selectedLabel(): string {
    const name = this.documentTypes.find(type => type.id === this.selectedDocumentTypeId)?.name;
    return name
      ? translateLookupLabel(this.transloco, this.documentTypeLabelPrefix, name)
      : this.transloco.translate('compliance.shared.modals.linkDocumentType.placeholder');
  }

  get isSaveDisabled(): boolean {
    return this.isLoading || this.selectedDocumentTypeId === null;
  }

  ngOnInit(): void {
    void this.loadDocumentTypesAsync();
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  /** Name of the requirement this type is already linked to, or null when selectable. */
  getLinkedRequirementName(type: VehicleDocumentType): string | null {
    return this.usedTypes.find(used => used.documentTypeId === type.id)?.requirementName ?? null;
  }

  selectDocumentType(type: VehicleDocumentType): void {
    if (this.getLinkedRequirementName(type)) {
      return;
    }
    this.selectedDocumentTypeId = type.id;
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    const selected = this.documentTypes.find(type => type.id === this.selectedDocumentTypeId);
    if (this.isSaveDisabled || !selected) {
      return;
    }

    const result: LinkDocumentTypeResult = {
      documentTypeId: selected.id,
      documentTypeName: selected.name
    };
    this.activeModal.close(result);
  }

  private async loadDocumentTypesAsync(): Promise<void> {
    try {
      const response = await this.vehicleDocumentsService.getDocumentTypes(this.cancellation, true);
      this.documentTypes = response.data ?? [];
    } catch (error) {
      this.errorService.handleError(error, { showToast: true });
    } finally {
      this.isLoading = false;
    }
  }
}
